import { apiRequest } from '../../shared/api/client';

/**
 * Payment terms — the current product's `tblWebTerm` and its maintenance screen.
 *
 *   GET    /api/v1/payment-terms[?includeInactive=true]   any signed-in user
 *   POST   /api/v1/payment-terms                          tenant:manage_settings
 *   PUT    /api/v1/payment-terms/{id}                     tenant:manage_settings
 *   DELETE /api/v1/payment-terms/{id}                     tenant:manage_settings
 *
 * Reading is open to any signed-in user because the supplier form has to offer the list.
 *
 * Nothing is seeded: terms live in the per-tenant database with their own screen, so each customer's
 * list is their own and a new tenant starts empty — which is how the current product behaves.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface PaymentTermDto {
  paymentTermId: string;
  paymentTermCode: string;
  displayName: string;
  /** Days until due, or null for a term that implies no due date — as every legacy one does. */
  netDays: number | null;
  isActive: boolean;
  /** A supplier is on it, so it can only be deactivated, never deleted. */
  isInUse: boolean;
}

export interface PaymentTermRequest {
  paymentTermCode: string;
  displayName: string;
  netDays: number | null;
  isActive: boolean;
}

export async function listPaymentTerms(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<PaymentTermDto[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<PaymentTermDto[]>(`/v1/payment-terms${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createPaymentTerm(body: PaymentTermRequest): Promise<PaymentTermDto> {
  const { data } = await apiRequest<PaymentTermDto>('/v1/payment-terms', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updatePaymentTerm(
  id: string,
  body: PaymentTermRequest,
): Promise<PaymentTermDto> {
  const { data } = await apiRequest<PaymentTermDto>(
    `/v1/payment-terms/${encodeURIComponent(id)}`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

export async function deletePaymentTerm(id: string): Promise<void> {
  await apiRequest<void>(`/v1/payment-terms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

/** How a term reads in a picker: `NET30 — Net 30`. */
export function paymentTermLabel(term: PaymentTermDto): string {
  return `${term.paymentTermCode} — ${term.displayName}`;
}
